import {
  CheckCircleOutlined,
  KeyOutlined,
  LinkOutlined,
  LoginOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { Drawer, Empty, Skeleton, Tag, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';
import { api, type SpeakerRow } from '../lib/api';

dayjs.extend(relativeTime);

const { Text } = Typography;

type ActivityRow = {
  id: string;
  type:
    | 'invite-sent'
    | 'invite-accepted'
    | 'magic-link-requested'
    | 'login-magic'
    | 'login-password';
  meta: { ip?: string; ua?: string; platform?: string } | null;
  created_at: number;
  event_id: string | null;
  event_title: string | null;
};

const LABEL: Record<ActivityRow['type'], string> = {
  'invite-sent': 'Invite email sent',
  'invite-accepted': 'Invite accepted',
  'magic-link-requested': 'Sign-in link requested',
  'login-magic': 'Signed in via email link',
  'login-password': 'Signed in with password',
};

const COLOR: Record<ActivityRow['type'], string> = {
  'invite-sent': 'blue',
  'invite-accepted': 'green',
  'magic-link-requested': 'gold',
  'login-magic': 'green',
  'login-password': 'green',
};

const ICON: Record<ActivityRow['type'], React.ReactNode> = {
  'invite-sent': <MailOutlined />,
  'invite-accepted': <CheckCircleOutlined />,
  'magic-link-requested': <LinkOutlined />,
  'login-magic': <LoginOutlined />,
  'login-password': <KeyOutlined />,
};

function compactUA(ua?: string): string | null {
  if (!ua) return null;
  // Heuristic browser/OS extraction so we don't dump a 200-char string.
  let browser = 'Browser';
  if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('OPR/')) browser = 'Opera';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Safari/')) browser = 'Safari';
  let os = '';
  if (ua.includes('iPhone')) os = 'iPhone';
  else if (ua.includes('iPad')) os = 'iPad';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
}

export function SpeakerActivityDrawer({
  open,
  speaker,
  onClose,
}: {
  open: boolean;
  speaker: SpeakerRow | null;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !speaker) return;
    setLoading(true);
    void (async () => {
      try {
        setRows(await api<ActivityRow[]>(`/api/speakers/${speaker.id}/activity`));
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, speaker]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Activity — ${speaker?.name ?? ''}`}
      width={520}
    >
      {speaker && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          {speaker.email}
        </Text>
      )}
      {loading ? (
        <Skeleton paragraph={{ rows: 6 }} active />
      ) : rows.length === 0 ? (
        <Empty description="No activity logged yet." />
      ) : (
        <Timeline
          items={rows.map((r) => {
            const browser = compactUA(r.meta?.ua ?? undefined);
            return {
              color: COLOR[r.type],
              dot: ICON[r.type],
              children: (
                <div>
                  <Text strong>{LABEL[r.type]}</Text>
                  <div style={{ marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(r.created_at).fromNow()} ·{' '}
                      {dayjs(r.created_at).format('D MMM YYYY HH:mm')}
                    </Text>
                  </div>
                  {r.event_title && (
                    <div style={{ marginTop: 4 }}>
                      <Tag>{r.event_title}</Tag>
                    </div>
                  )}
                  {(browser || r.meta?.ip) && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                      {browser}
                      {browser && r.meta?.ip ? ' · ' : ''}
                      {r.meta?.ip}
                    </div>
                  )}
                </div>
              ),
            };
          })}
        />
      )}
    </Drawer>
  );
}
